/**
 * PQL — Priorities Query Language: AST types.
 *
 * The AST is a strict superset of the item list's two-level FilterGroup[]
 * shape (see src/lib/pql/interop.ts, which stays client-only). Leaves ARE
 * FilterRule objects — the same structure the filter builder produces and
 * saved reports persist — so the builder, the statement bar, and stored JSON
 * are three representations of one object (LARGE-SETS-QUERY-AND-SMART-SETS-PRD.md §2.1).
 *
 * CANONICAL LOCATION (migration to Phase 2, server-side evaluation): this
 * file — and every other file in this directory except interop.ts, which
 * never left src/lib/pql/ — is imported by BOTH runtimes: the Vite client via
 * src/lib/pql/{types,tokenizer,parser,printer,english,evaluate,catalog}.ts,
 * which are now thin `export *` shims pointing back here, and the Supabase
 * edge function (Deno) directly, e.g. from supabase/functions/api/routes/.
 * Because it must load identically in the browser bundle and in Deno, this
 * whole directory MUST stay dependency-free: zero repo imports, no browser
 * or Deno globals. (Same constraint, same reasoning, as the established
 * precedent at supabase/functions/_shared/intelligenceCores.ts.)
 *
 * FilterRule/SortSpec/FilterFieldType/FilterFieldOption are therefore OWNED
 * here, not imported from the item-list UI (src/components/.../useItemListState.ts,
 * config/itemFields.ts) the way they were pre-Phase-2 — Deno cannot reach
 * those files (React-adjacent, deep in src/components/). KEEP IN SYNC: if the
 * item-list's FilterRule/FilterOperator/SortSpec/FilterFieldType shape
 * changes, mirror the change here. This mirrors the repo's existing pattern
 * for the same constraint (see supabase/functions/api/lib/entitlements.ts
 * and judgment.ts's "Edge Function copy... KEEP IN SYNC" headers).
 */

export type FilterOperator =
  | 'in' | 'eq' | 'range' | 'boolean' | 'text' | 'date-range'
  | 'empty' // IS [NOT] EMPTY
  | 'gt' | 'lt' // strict numeric comparison — only matches items that HAVE a value
  | 'date-after' | 'date-before'; // strictly outside the given day — only matches items with a date

export interface FilterRule {
  id: string;
  field: string;
  label: string;
  operator: FilterOperator;
  /** date-range uses [isoStart, isoEnd] where empty string means unbounded */
  value: string | string[] | boolean | number | [number, number] | [string, string];
  displayValue: string;
}

export interface SortSpec {
  field: string;
  direction: 'asc' | 'desc';
}

export type FilterFieldType = 'set' | 'boolean' | 'text' | 'date-range' | 'range';

export interface FilterFieldOption {
  value: string;
  label: string;
  dot?: string;
}

// ============================================================================
// AST
// ============================================================================

export interface AndNode {
  op: 'and';
  children: QueryNode[];
}

export interface OrNode {
  op: 'or';
  children: QueryNode[];
}

export interface NotNode {
  op: 'not';
  child: QueryNode;
}

/** A query node: a boolean combinator or a FilterRule leaf. */
export type QueryNode = AndNode | OrNode | NotNode | FilterRule;

/** A full parsed query: optional predicate + optional sort. */
export interface PqlQuery {
  where: QueryNode | null;
  orderBy: SortSpec[];
}

export function isCombinator(node: QueryNode): node is AndNode | OrNode | NotNode {
  return 'op' in node && (node.op === 'and' || node.op === 'or' || node.op === 'not');
}

export function isLeaf(node: QueryNode): node is FilterRule {
  return !isCombinator(node);
}

// ============================================================================
// Field catalog (parser vocabulary)
// ============================================================================

/**
 * The vocabulary the parser validates against. Built from the same resolved
 * FilterFieldDef list the filter builder uses (config/filterFields.ts), so
 * "add a field to the registry → it exists in PQL" holds with no extra work.
 */
export interface PqlFieldSpec {
  /** Canonical field key — the itemFields registry key, or `meta:<key>` for custom fields. */
  key: string;
  label: string;
  type: FilterFieldType;
  options?: FilterFieldOption[];
}

export interface PqlContext {
  /** Resolves currentUser() in statements. Absent → currentUser() is a parse error. */
  currentUserId?: string;
  /** Clock for now() and relative durations (-30d). Defaults to the real clock. */
  now?: () => Date;
}

export interface PqlCatalog {
  fields: PqlFieldSpec[];
  context: PqlContext;
  /** Resolve a statement field name (key, alias, label, or meta.<key>) to its spec. */
  resolveField(name: string): PqlFieldSpec | undefined;
  /** Nearest-field suggestion for an unknown name, or undefined when nothing is close. */
  suggestField(name: string): string | undefined;
}

// ============================================================================
// Errors
// ============================================================================

export class PqlError extends Error {
  /** 0-based character offsets into the statement. */
  readonly start: number;
  readonly end: number;
  /** A concrete replacement hint, e.g. a near-miss field or valid value list. */
  readonly suggestion?: string;

  constructor(message: string, start: number, end: number, suggestion?: string) {
    super(message);
    this.name = 'PqlError';
    this.start = start;
    this.end = end;
    if (suggestion !== undefined) this.suggestion = suggestion;
  }
}

export type ParseResult =
  | { ok: true; query: PqlQuery }
  | { ok: false; error: PqlError };
